import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Ship, Clock, ArrowRight, Plus, Trash2, User } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { inr, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function BookingPage() {
  const { scheduleId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const cls = params.get("class") || "economy";
  const date = params.get("date");
  const pax = parseInt(params.get("pax") || "1", 10);

  const [sch, setSch] = useState(null);
  const [passengers, setPassengers] = useState(
    Array.from({ length: pax }, () => ({ name: "", age: "", gender: "male" }))
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/schedules/${scheduleId}`).then((r) => setSch(r.data)).catch(() => toast.error("Schedule not found"));
  }, [scheduleId]);

  const price = sch ? (cls === "economy" ? sch.economy_price : sch.business_price) : 0;
  const total = price * passengers.length;

  const update = (i, field, val) => {
    setPassengers((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: val } : p)));
  };
  const addPax = () => passengers.length < 6 && setPassengers([...passengers, { name: "", age: "", gender: "male" }]);
  const removePax = (i) => passengers.length > 1 && setPassengers(passengers.filter((_, idx) => idx !== i));

  const submit = async () => {
    for (const p of passengers) {
      if (!p.name.trim() || !p.age) return toast.error("Please fill in all passenger details");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        schedule_id: scheduleId,
        travel_date: date,
        travel_class: cls,
        passengers: passengers.map((p) => ({ name: p.name.trim(), age: parseInt(p.age, 10), gender: p.gender })),
      });
      navigate(`/checkout/${data.id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!sch) return <div className="min-h-screen bg-background"><Navbar /></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 grid gap-6 lg:grid-cols-[1fr_340px]">
        <div>
          <p className="overline text-accent mb-1">Passenger details</p>
          <h1 className="font-display font-bold text-2xl text-primary tracking-tight mb-6">Who's travelling?</h1>

          <div className="space-y-4">
            {passengers.map((p, i) => (
              <Card key={i} data-testid={`passenger-card-${i}`}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                  <CardTitle className="text-sm flex items-center gap-2 text-primary">
                    <User className="h-4 w-4 text-accent" /> Passenger {i + 1}
                  </CardTitle>
                  {passengers.length > 1 && (
                    <button onClick={() => removePax(i)} className="text-muted-foreground hover:text-destructive" data-testid={`remove-pax-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </CardHeader>
                <CardContent className="grid gap-3 sm:grid-cols-[1fr_100px_130px]">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Full name</Label>
                    <Input value={p.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="e.g. Priya Sharma" data-testid={`pax-name-${i}`} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Age</Label>
                    <Input type="number" min="0" value={p.age} onChange={(e) => update(i, "age", e.target.value)} placeholder="30" data-testid={`pax-age-${i}`} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Gender</Label>
                    <Select value={p.gender} onValueChange={(v) => update(i, "gender", v)}>
                      <SelectTrigger data-testid={`pax-gender-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {passengers.length < 6 && (
            <Button variant="outline" className="mt-4" onClick={addPax} data-testid="add-passenger-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add passenger
            </Button>
          )}
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-24 h-fit">
          <Card data-testid="booking-summary">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-primary">Trip summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Ship className="h-3.5 w-3.5 text-accent" /> {sch.ferry_name}
              </div>
              <div className="flex items-center gap-3">
                <div>
                  <div className="font-display font-bold text-lg text-primary">{sch.departure_time}</div>
                  <div className="text-xs text-muted-foreground">{sch.from_port?.city}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-accent" />
                <div>
                  <div className="font-display font-bold text-lg text-primary">{sch.arrival_time}</div>
                  <div className="text-xs text-muted-foreground">{sch.to_port?.city}</div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" /> {date && format(new Date(date), "EEE, dd MMM yyyy")}
              </div>
              <div className="border-t border-border pt-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground capitalize">{cls} × {passengers.length}</span>
                  <span>{inr(total)}</span>
                </div>
                <div className="flex justify-between font-display font-bold text-lg text-primary pt-1">
                  <span>Total</span>
                  <span data-testid="summary-total">{inr(total)}</span>
                </div>
              </div>
              <Button className="w-full bg-accent hover:bg-accent/90 text-white font-semibold" onClick={submit} disabled={submitting} data-testid="proceed-payment-btn">
                {submitting ? "Please wait…" : "Continue to payment"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
